import React, { useRef, useState } from 'react';
import Button from './Button';

/**
 * File upload component for avatar/poster images
 * Design: High-contrast, drag-and-drop zone
 */
export default function FileUpload({
  label,
  accept = 'image/*',
  maxSize = 5 * 1024 * 1024, // 5MB default
  value,
  onChange,
  onError,
  preview = true,
  aspectRatio = 'square', // 'square', 'landscape', 'portrait'
  className = '',
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || null);

  // Sync with value prop changes
  React.useEffect(() => {
    setPreviewUrl(value || null);
  }, [value]);

  const aspectRatios = {
    square: 'aspect-square',
    landscape: 'aspect-video',
    portrait: 'aspect-[3/4]',
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file size
    if (file.size > maxSize) {
      onError?.(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    onChange?.(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onChange?.(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
          {label}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="sr-only"
      />

      {previewUrl && preview ? (
        /* Preview state */
        <div className="relative border-2 border-black">
          <div className={`${aspectRatios[aspectRatio]} overflow-hidden bg-gray-100`}>
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-white border-t-2 border-black flex justify-between items-center">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => inputRef.current?.click()}
            >
              CHANGE
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemove}
            >
              REMOVE
            </Button>
          </div>
        </div>
      ) : (
        /* Upload zone */
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`
            ${aspectRatios[aspectRatio]}
            border-2 border-dashed
            flex flex-col items-center justify-center
            cursor-pointer
            transition-colors duration-200
            ${isDragging
              ? 'border-black bg-gray-100'
              : 'border-gray-400 bg-white hover:border-black hover:bg-gray-50'
            }
          `}
        >
          {/* Upload icon */}
          <div className="w-12 h-12 mb-4 border-2 border-black flex items-center justify-center">
            <svg
              className="w-6 h-6 text-black"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>

          <p className="font-mono text-sm font-semibold uppercase tracking-wider text-black mb-1">
            {isDragging ? 'DROP FILE HERE' : 'CLICK OR DRAG TO UPLOAD'}
          </p>
          <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
            MAX {Math.round(maxSize / 1024 / 1024)}MB
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Avatar-specific upload component
 */
export function AvatarUpload({
  value,
  onChange,
  onError,
  size = 'md',
  className = '',
}) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(value || null);

  // Sync with value prop changes
  React.useEffect(() => {
    setPreviewUrl(value || null);
  }, [value]);

  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      onError?.('Avatar must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    onChange?.(file);
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="sr-only"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`
          ${sizes[size]}
          border-2 border-black
          overflow-hidden
          flex items-center justify-center
          bg-gray-100
          hover:bg-gray-200
          transition-colors duration-200
        `}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Avatar"
            className="w-full h-full object-cover"
          />
        ) : (
          <svg
            className="w-1/3 h-1/3 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="square"
              strokeLinejoin="miter"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}
